import { calculateOrderItemTotal } from "@lemmonhq/orders";
import {
  Order,
  OrderItem,
  OrderItemGroup,
  OrderItemTaxGroup,
  Printer,
  PrinterPayemnt,
  RestaurantProfile,
} from "@lemmonhq/types";

import { Context } from "hono";

const objectToPathString = (obj: Record<string, string | number>) => {
  return Object.entries(obj)
    .map(([key, value]) => `${key}/${value}`)
    .join("/");
};

export const getKitchenTicketPrintJobId = (
  order: Order,
  printer: Printer,
  triggeredFoodTypeIds?: number[],
) => {
  return objectToPathString({
    type: "kitchen",
    order: order.id.toString(),
    printer: printer.id.toString(),
    ...(triggeredFoodTypeIds && triggeredFoodTypeIds.length > 0
      ? { courses: triggeredFoodTypeIds.join(",") }
      : {}),
    time: Date.now() / 1000,
  });
};

export const getTaxRcptPrintJobId = (payment: { id: number | string }) => {
  return objectToPathString({
    type: "reciept",
    payment: payment.id.toString(),
    time: Date.now() / 1000,
  }).toString();
};

export function getTaxRates(orderItems: OrderItem[]) {
  const taxRates = orderItems.filter((el) => el.tax_rate);
  const taxRateMap = new Map<
    string,
    { id: string; type: string; value: number }
  >();

  taxRates.forEach((el, index) => {
    if (el.tax_rate && !taxRateMap.has(el.tax_rate.id)) {
      taxRateMap.set(el.tax_rate.id, {
        id: el.tax_rate.id,
        type: el.tax_rate.type,
        value: el.tax_rate.value,
      });
    }
  });

  return Array.from(taxRateMap.values()).map((rate, index) => ({
    ...rate,
    label: String.fromCharCode(index + 65), // 'A', 'B', 'C', etc.
  }));
}

export function getTaxRateMd(
  payment: PrinterPayemnt,
  groupItems: OrderItemGroup[],
  taxRates: OrderItemTaxGroup[],
) {
  let vatSection = "";
  vatSection = taxRates
    .map((t) => {
      const matchingItems = groupItems.filter((el) => el.taxLabel === t.label);
      const sum = matchingItems.reduce(
        (s, i) => s + calculateOrderItemTotal(i),
        0,
      );
      const net = sum / (1 + t.value / 100);
      const vat = sum - net;
      return [
        `-------------------------------------`,
        `{width:8,13,13,13}`,
        `TVA ${t.label} | NET | TVA | BRUT|`,
        `${t.value.toFixed(2)}% |CHF ${net.toFixed(2)}|CHF ${vat.toFixed(
          2,
        )}| CHF ${sum.toFixed(2)}`,
      ].join("\n");
    })
    .join("\n\n");
  return vatSection;
}

export function groupOrderItems(
  orderItems: OrderItem[],
  partial?: { [orderItemId: string]: number },
): { groups: OrderItemGroup[]; taxRates: OrderItemTaxGroup[] } {
  const groupedItems: Record<string, OrderItemGroup> = {};
  const taxRates = getTaxRates(orderItems);
  for (const item of orderItems) {
    const groupId = getOrderItemGroupId(item);
    // console.log("groupId", groupId, item);
    const taxRate = taxRates.find((el) => el.id === item.tax_rate_id);

    if (!groupedItems[groupId]) {
      groupedItems[groupId] = {
        quantity: 0,
        product: item.product,
        bundles: item.bundles ?? null,
        price: item.price,
        taxLabel: taxRate?.label,
      };
    }
    const qty = partial ? partial[item.id] : item.quantity;
    if (qty) {
      groupedItems[groupId].quantity += qty;
    }
  }
  // todo: understand why filter is needed heere
  return {
    groups: Object.values(groupedItems).filter((el) => el.quantity > 0),
    taxRates: taxRates,
  };
}

// TODO: add price of option as inidividual line item if it's > 0
export const getOrderItemDescription = (item: OrderItemGroup) => {
  return (
    item.product.name +
    (item.bundles
      ? `[${item.price.toFixed(2)}]\\n${item.bundles
          .map(
            (el) => `- ${el.display.title} [+${(el.price || 0.0).toFixed(2)}]`,
          )
          .join("\\n")}`
      : "")
  );
};

export function getOrderItemGroupId(orderItem: OrderItem): string {
  const baseId = `product:${orderItem.product_id}`;

  if (orderItem.product.is_custom) {
    return baseId + ":custom:" + orderItem.price;
  }

  if (!orderItem.bundles || orderItem.bundles.length === 0) {
    return baseId;
  }

  const bundleIds = orderItem.bundles
    .map((bundle) => `bundle:${bundle.entity_type}:${bundle.entity_id}`)
    .sort() // Ensures order consistency
    .join("|");

  const rawString = `${baseId}|${bundleIds}`;
  const hash = btoa(unescape(encodeURIComponent(rawString))) // Simple browser-safe hash
    .replace(/[^a-zA-Z0-9]/g, ""); // Remove non-alphanumeric characters

  return hash;
}

export const createLogoMarkdown = (
  restaurant: RestaurantProfile,
  logo?: string | null,
) => {
  return logo ? `{image:${logo}}` : `^^^${restaurant.name}`;
};

export const getRestaurantLogo = async (
  c: Context,
  restaurant: RestaurantProfile,
): Promise<string | null> => {
  // return null;
  const env = c.env;
  //  return null;
  //console.log("restaurant", restaurant);
  console.log(`restaurant.receipt_logo = ${restaurant.receipt_logo}`);
  if (!restaurant.receipt_logo) return null;

  const imageURL = `${env.R2_PUBLIC_HOST}/${env.ENV}/${restaurant.id}/receipt/${restaurant.receipt_logo}`;
  console.log(`imageURL = ${imageURL}`);
  try {
    // cf.image applies Cloudflare Image Transformations in-Worker (cdn-cgi/image subrequests
    // are blocked within the same zone and only work from a browser).
    // format: "png" is NOT a hard guarantee — CF auto-selects JPEG for opaque
    // images (see the `cf-images 299` warning header), so "png-force" is
    // required here since the ESC/POS renderer decodes this with pngjs, which
    // rejects anything without a real PNG signature.
    const response = await fetch(imageURL, {
      signal: AbortSignal.timeout(2000),
      // @ts-ignore — cf is a Cloudflare Workers-specific fetch option
      cf: { image: { width: 400, fit: "scale-down", format: "png-force" } },
    });
    if (!response.ok) {
      console.error(
        `getRestaurantLogo ${restaurant.name} failed fetching imageURL=` +
          imageURL,
      );

      return null;
    }
    const contentType = response.headers.get("content-type") ?? "";
    console.log(`contentType = ${contentType}`);
    if (!contentType.startsWith("image/")) {
      const text = await response.text().catch(() => "<non-text body>");
      console.error("Expected image, got:", {
        contentType,
        status: response.status,
        text: text.slice(0, 200),
      });
      return null;
    }

    const arrayBuffer = await response.arrayBuffer();

    // Validate PNG magic bytes (89 50 4E 47 0D 0A 1A 0A) to catch cases where
    // CF Image Transformations returns a non-PNG body despite image/png content-type.
    const bytes = new Uint8Array(arrayBuffer);
    const firstBytes = bytes.slice(0, 16);
    console.log("getRestaurantLogo: response bytes", {
      byteLength: bytes.length,
      firstBytes: Array.from(firstBytes),
      firstBytesHex: Array.from(firstBytes, (byte) =>
        byte.toString(16).padStart(2, "0"),
      ).join(" "),
    });
    if (
      bytes.length < 8 ||
      bytes[0] !== 0x89 ||
      bytes[1] !== 0x50 ||
      bytes[2] !== 0x4e ||
      bytes[3] !== 0x47
    ) {
      console.error("getRestaurantLogo: response is not a valid PNG", {
        first4: Array.from(bytes.slice(0, 4)),
        contentType,
      });
      return null;
    }

    const base64String = arrayBufferToBase64(arrayBuffer);

    return base64String;
  } catch (e) {
    console.error("getRestaurantLogo", e);
    return null;
  }
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}
